// src/pages/Account/SettingsPage.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import SideNavAccount from "../../components/SideNav/SideNavAccount";
import { useMealMate } from "../../context/MealMateContext";
import api from "../../api";
import {
  App,
  Switch,
  TimePicker,
  Button,
  Spin,
  Modal,
  Checkbox,
  Row,
  Col,
  InputNumber,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const SettingsPageWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 2.5rem 3rem;
  background-color: #f8f9fa;

  h1 {
    font-size: 2.8rem;
    color: #2c3e50;
    margin-bottom: 2.5rem;
    text-align: center;
    font-weight: 700;
    letter-spacing: -0.05rem;
  }
`;

const SettingsSection = styled.div`
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;

  h2 {
    font-size: 1.8rem;
    color: #34495e;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 0.8rem;
  }

  .setting-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
      border-bottom: none;
    }

    .setting-label {
      font-size: 1.1rem;
      color: #555;
    }
  }
`;

const ReminderSetting = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;

  .ant-row {
    align-items: center;
  }
  .ant-checkbox-wrapper span {
    font-size: 1rem;
  }
`;

const createDayjsFromTime = (timeString) => {
  if (timeString && typeof timeString === "string") {
    const parsedTime = dayjs(timeString, "HH:mm");
    if (parsedTime.isValid()) {
      return parsedTime;
    }
  }
  return dayjs().hour(0).minute(0).second(0);
};

const SettingsPage = () => {
  const { message: antdMessage } = App.useApp();
  const { user, loading, isAuthenticated, setUser, fetchUserProfile } = useMealMate();
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [mealReminders, setMealReminders] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const DEFAULT_MEAL_REMINDERS = useMemo(
    () => [
      {
        mealType: "breakfast",
        time: createDayjsFromTime("07:00"),
        enabled: false,
        reminderMinutes: 10,
      },
      {
        mealType: "lunch",
        time: createDayjsFromTime("12:00"),
        enabled: false,
        reminderMinutes: 10,
      },
      {
        mealType: "dinner",
        time: createDayjsFromTime("18:00"),
        enabled: false,
        reminderMinutes: 10,
      },
      {
        mealType: "snack",
        time: createDayjsFromTime("15:00"),
        enabled: false,
        reminderMinutes: 10,
      },
    ],
    []
  );

  useEffect(() => {
    console.log("🧪 [DEBUG] useEffect trong SettingsPage chạy. user:", user);
    console.log("🧪 [DEBUG] loading từ Context:", loading);
    console.log("🧪 [DEBUG] isAuthenticated:", isAuthenticated);

    const initializeSettings = async () => {
      if (loading) {
        console.log("🧪 [DEBUG] Context đang loading, giữ pageLoading true.");
        setPageLoading(true);
        return;
      }

      if (!isAuthenticated) {
        console.log("🧪 [DEBUG] Người dùng chưa đăng nhập, hiển thị thông báo lỗi.");
        setNotificationEnabled(false);
        setMealReminders(DEFAULT_MEAL_REMINDERS);
        setPageLoading(false);
        antdMessage.error("Vui lòng đăng nhập để xem cài đặt thông báo.");
        return;
      }

      if (!user || !user.notificationSettings) {
        console.log("🧪 [DEBUG] User hoặc notificationSettings chưa sẵn sàng, gọi fetchUserProfile.");
        await fetchUserProfile();
        setPageLoading(true); // Giữ pageLoading cho đến khi fetch hoàn tất
        return;
      }

      console.log("🧪 [DEBUG] User tồn tại, khởi tạo dữ liệu.");
      console.log("🧪 [DEBUG] user.notificationSettings:", user.notificationSettings);

      setNotificationEnabled(user.notificationSettings.enabled);
      const initialReminders = user.notificationSettings.mealReminders.map(reminder => ({
        ...reminder,
        time: createDayjsFromTime(reminder.time),
      }));
      setMealReminders(initialReminders);
      setPageLoading(false);
    };

    initializeSettings();
  }, [user, loading, isAuthenticated, antdMessage, DEFAULT_MEAL_REMINDERS, fetchUserProfile]);

  const handleNotificationToggle = useCallback((checked) => {
    setNotificationEnabled(checked);
  }, []);

  const handleMealReminderToggle = useCallback((mealType, checked) => {
    setMealReminders((prevReminders) =>
      prevReminders.map((reminder) =>
        reminder.mealType === mealType
          ? { ...reminder, enabled: checked }
          : reminder
      )
    );
  }, []);

  const handleMealReminderChange = useCallback(
    (mealType, field, value) => {
      setMealReminders((prevReminders) =>
        prevReminders.map((reminder) =>
          reminder.mealType === mealType ? { ...reminder, [field]: value } : reminder
        )
      );
    },
    []
  );

  const handleSaveSettings = useCallback(async () => {
    setPageLoading(true);

    try {
      if (!mealReminders || mealReminders.length === 0) {
        antdMessage.error("Không có dữ liệu nhắc nhở bữa ăn để lưu.");
        setPageLoading(false);
        return;
      }

      const formattedMealReminders = mealReminders.map((r) => ({
        mealType: r.mealType,
        time: r.time && r.time.isValid() ? r.time.format("HH:mm") : "00:00",
        enabled: Boolean(r.enabled),
        reminderMinutes: r.reminderMinutes || 10,
      }));

      const typesArray = [];
      if (notificationEnabled && formattedMealReminders.some((r) => r.enabled)) {
        typesArray.push("meal_reminder");
      }

      const payload = {
        enabled: Boolean(notificationEnabled),
        frequency: user?.notificationSettings?.frequency || "daily",
        types: typesArray,
        mealReminders: formattedMealReminders,
        generalReminder: user?.notificationSettings?.generalReminder || {
          enabled: false,
          time: "09:00",
        },
      };

      console.log("🚚 Gửi payload cập nhật:", payload);
      const response = await api.put("/users/notification-settings", payload);
      console.log("✅ Phản hồi API sau khi lưu:", response.data);

      if (response.data && response.status === 200) {
        const updatedUser = {
          ...user,
          notificationSettings: {
            enabled: payload.enabled,
            frequency: payload.frequency,
            types: payload.types,
            mealReminders: payload.mealReminders,
            generalReminder: payload.generalReminder,
          },
        };
        setUser(updatedUser);
        sessionStorage.setItem("userInfo", JSON.stringify(updatedUser));
        antdMessage.success("Cài đặt thông báo đã được lưu thành công!");
        setShowModal(false);
      } else {
        console.warn("API response không trả về user đầy đủ:", response.data);
        antdMessage.success("Cài đặt đã được lưu, nhưng phản hồi user không đầy đủ!");
        setShowModal(false);
      }
    } catch (error) {
      console.error("Lỗi khi lưu cài đặt thông báo:", error);
      let errorMessage = "Lỗi khi lưu cài đặt thông báo.";
      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          `Lỗi server: ${error.response.status}`;
      } else if (error.request) {
        errorMessage =
          "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng.";
      } else {
        errorMessage = error.message;
      }
      antdMessage.error(errorMessage);
    } finally {
      setPageLoading(false);
    }
  }, [notificationEnabled, mealReminders, setUser, antdMessage, user]);

  if (pageLoading || loading) {
    return (
      <SettingsPageWrapper>
        <SideNavAccount />
        <MainContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
        </MainContent>
      </SettingsPageWrapper>
    );
  }

  return (
    <SettingsPageWrapper>
      <SideNavAccount />
      <MainContent>
        <h1>Cài Đặt Thông Báo</h1>

        <SettingsSection>
          <h2>Thiết lập chung</h2>
          <div className="setting-item">
            <span className="setting-label">Bật tất cả thông báo</span>
            <Switch
              checked={notificationEnabled}
              onChange={handleNotificationToggle}
              style={{ backgroundColor: notificationEnabled ? "#6a8bad" : "" }}
            />
          </div>
        </SettingsSection>

        {notificationEnabled && (
          <SettingsSection>
            <h2>Nhắc nhở bữa ăn</h2>
            {mealReminders.map((reminder) => (
              <ReminderSetting key={reminder.mealType}>
                <Row gutter={16} align="middle">
                  <Col span={8}>
                    <Checkbox
                      checked={reminder.enabled}
                      onChange={(e) =>
                        handleMealReminderToggle(
                          reminder.mealType,
                          e.target.checked
                        )
                      }
                      disabled={!notificationEnabled}
                    >
                      Nhắc nhở{" "}
                      {reminder.mealType === "breakfast"
                        ? "Bữa sáng"
                        : reminder.mealType === "lunch"
                        ? "Bữa trưa"
                        : reminder.mealType === "dinner"
                        ? "Bữa tối"
                        : "Bữa phụ"}
                    </Checkbox>
                  </Col>
                  <Col span={8}>
                    <TimePicker
                      value={reminder.time}
                      onChange={(time) =>
                        handleMealReminderChange(reminder.mealType, "time", time)
                      }
                      format="HH:mm"
                      minuteStep={5}
                      use12Hours={false}
                      disabled={!reminder.enabled || !notificationEnabled}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={0}
                      max={60}
                      value={reminder.reminderMinutes}
                      onChange={(value) =>
                        handleMealReminderChange(
                          reminder.mealType,
                          "reminderMinutes",
                          value
                        )
                      }
                      disabled={!reminder.enabled || !notificationEnabled}
                      formatter={(value) => `${value} phút trước`}
                      parser={(value) => value.replace(" phút trước", "")}
                      style={{ width: "100%" }}
                      step={1}
                    />
                  </Col>
                </Row>
              </ReminderSetting>
            ))}
          </SettingsSection>
        )}

        <div style={{ textAlign: "right", marginTop: "1.5rem" }}>
          <Button
            type="primary"
            onClick={() => setShowModal(true)}
            loading={pageLoading}
            style={{ backgroundColor: "#6a8bad", borderColor: "#6a8bad" }}
          >
            Lưu cài đặt thông báo
          </Button>
        </div>

        <Modal
          title="Xác nhận lưu cài đặt"
          open={showModal}
          onOk={handleSaveSettings}
          onCancel={() => setShowModal(false)}
          okText="Có, lưu ngay"
          cancelText="Hủy"
          confirmLoading={pageLoading}
          okButtonProps={{
            style: { backgroundColor: "#6a8bad", borderColor: "#6a8bad" },
          }}
        >
          <p>Bạn có chắc chắn muốn lưu các cài đặt thông báo này không?</p>
        </Modal>
      </MainContent>
    </SettingsPageWrapper>
  );
};

export default SettingsPage;